import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import * as R from 'Router/routes';
import ChevronIcon from 'assets/icons/chevron-left.svg?react';
import { useClientQuery, usePSD2RequestedQuery } from 'hooks/webapi';
import {
  Button,
  Card,
  Link,
  Placeholder,
  Typography,
} from '../../../components';
import styles from './OCRResults.module.scss';

export default function OCRResults() {
  const { t } = useTranslation(['ocr_results', 'common']);
  const navigate = useNavigate();
  const { data: client, isLoading } = useClientQuery();

  const [shouldCheckPsd2, setShouldCheckPsd2] = useState(false);
  const { data: psd2RequestedData } = usePSD2RequestedQuery(shouldCheckPsd2);

  useEffect(() => {
    if (!shouldCheckPsd2 || !psd2RequestedData) {
      return;
    }

    if (psd2RequestedData.psd2requested) {
      navigate(R.PSD2_BANKS);
    } else {
      navigate(R.MANUAL_IDENTIFICATION);
    }
  }, [setShouldCheckPsd2, psd2RequestedData, shouldCheckPsd2, navigate]);

  const handleClickConfirm = useCallback(async () => {
    setShouldCheckPsd2(true);
  }, []);

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <div className={styles['box-wrapper']}>
        <Typography className={styles.title} size="xl" kind="h1">
          {t('title')}
        </Typography>
        <Typography className={styles.info}>
          <span dangerouslySetInnerHTML={{ __html: t('info') }} />
        </Typography>
      </div>

      <Card className={styles.card}>
        <div className={styles['table-header']}>
          <Typography size="xs">{t('fullName')}</Typography>
          <Typography className={styles['table-title']} size="l">
            <Placeholder loading={isLoading}>{client?.fullName}</Placeholder>
          </Typography>
        </div>
        <div className={styles.table}>
          <div className={styles.row}>
            <Typography size="xs">{t('personal_id')}</Typography>
            <Typography className={styles.value}>
              <Placeholder loading={isLoading}>
                {client?.personalId}
              </Placeholder>
            </Typography>
          </div>
          <div className={styles.row}>
            <Typography size="xs">{t('dob')}</Typography>
            <Typography className={styles.value}>
              <Placeholder loading={isLoading}>
                {dayjs(client?.dateOfBirth).format('DD.MM.YYYY')}
              </Placeholder>
            </Typography>
          </div>
          <div className={styles.row}>
            <Typography size="xs">{t('id_card')}</Typography>
            <Typography className={styles.value}>
              <Placeholder loading={isLoading}>
                {client?.identityDocumentId}
              </Placeholder>
            </Typography>
          </div>
          <div className={styles.row}>
            <Typography size="xs">{t('address')}</Typography>
            <Typography className={styles.value}>
              <Placeholder loading={isLoading}>
                {`${client?.actualAddress.fullAddress}, ${client?.actualAddress.postalCode} ${client?.actualAddress.location2}`}
              </Placeholder>
            </Typography>
          </div>
        </div>
      </Card>

      <div className={styles['box-wrapper']}>
        <Button className={styles.confirm} onClick={handleClickConfirm}>
          {t('common:confirm')}
        </Button>
        <div className={styles.link}>
          <Link to={R.MANUAL_OCR}>
            <ChevronIcon className={styles.chevron} />
            {t('common:back')}
          </Link>
        </div>
      </div>
    </>
  );
}
